import { createContext, useCallback, useEffect, useMemo, useReducer } from "react";

export const FeatureDefinitionsContext = createContext({
  definitions: [],
  isLoading: false,
  error: null,
  fetchDefinitions: () => {},
});

const ACTION_TYPES = {
  SET_LOADING: "SET_LOADING",
  SET_DEFINITIONS: "SET_DEFINITIONS",
  SET_ERROR: "SET_ERROR",
};

const initialState = {
  definitions: [],
  isLoading: false,
  error: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return { ...state, isLoading: true, error: null };

    case ACTION_TYPES.SET_DEFINITIONS:
      return {
        ...state,
        definitions: action.payload,
        isLoading: false,
        error: null,
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        definitions: [],
        isLoading: false,
        error: action.payload,
      };

    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

export const FeatureDefinitionsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchDefinitions = useCallback(async () => {
    dispatch({ type: ACTION_TYPES.SET_LOADING });

    try {
      const response = await fetch(
        // `http://localhost:3000/feature-definitions`
        `${process.env.REACT_APP_BACKEND_URL}/feature-definitions`
      );

      if (!response.ok) {
        throw new Error("Could not fetch feature definitions.");
      }

      const data = await response.json();

      dispatch({
        type: ACTION_TYPES.SET_DEFINITIONS,
        payload: Array.isArray(data.definitions) ? data.definitions : [],
      });
    } catch (err) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: err.message || "Something went wrong fetching definitions.",
      });
    }
  }, []);

  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);

  const value = useMemo(
    () => ({
      definitions: state.definitions,
      isLoading: state.isLoading,
      error: state.error,
      fetchDefinitions,
    }),
    [state.definitions, state.isLoading, state.error, fetchDefinitions]
  );

  return (
    <FeatureDefinitionsContext.Provider value={value}>
      {children}
    </FeatureDefinitionsContext.Provider>
  );
};